import {
  Component,
  ElementRef,
  ViewChild,
  Input,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  inject,
  HostBinding,
  OnDestroy,
  ChangeDetectionStrategy
} from '@angular/core';

import * as d3 from 'd3';
import { ThemeService } from '../../../../core/services/theme.service';
import { Subscription } from 'rxjs';

export interface BarSegment {
  value: number;
  color: string;
  label: string; // For tooltip
}

export interface BarData {
  label: string;
  tooltipLabel?: string;
  value: number; // Total value (sum of segments if stacked, or single value)
  color?: string;
  gradientStart?: string;
  gradientEnd?: string;
  segments?: BarSegment[]; // For stacked bars
  displayValue?: string; // Custom string for the right-side value
  tooltipTotalValue?: string | number; // Custom total value for tooltip
  tooltipValueLabel?: string; // Custom full text for the tooltip's value row (overrides the formatted value + xAxisTitle)
}

@Component({
  selector: 'app-horizontal-bar-chart',
  standalone: true,
  templateUrl: './horizontal-bar-chart.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./horizontal-bar-chart.component.scss'],
})
export class HorizontalBarChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @HostBinding('class') hostClass = 'block w-full';
  @ViewChild('chartContainer', { static: true }) chartContainer!: ElementRef;
  @Input() data: BarData[] = [];
  @Input() leftMargin: number = 0;
  @Input() showYAxisGrid: boolean = false;
  @Input() showXAxisGrid: boolean = false;
  @Input() showXAxisLabel: boolean = false;
  @Input() showYAxisLabel: boolean = true;
  @Input() showYAxisLine: boolean = true;
  @Input() showBarsValue: boolean = false;
  @Input() scaleType: 'linear' | 'sqrt' = 'linear'; // New: scale type for x-axis
  @Input() currentTheme: 'light' | 'dark' = 'dark';
  @Input() minHeight: number = 250;
  @Input() barHeight: number = 45;
  @Input() marginTop: number = 30;
  @Input() marginBottom: number = 40;
  @Input() barThickness?: number;
  @Input() barPadding: number = 0.35;
  @Input() showLegend: boolean = false;
  @Input() showTooltip: boolean = true;
  @Input() legendPosition: 'top' | 'bottom' = 'bottom';
  @Input() xAxisTitle?: string;
  @Input() maxBarWidth: number = 0;
  @Input() showStripes: boolean = false; // When true, adds diagonal hatch pattern overlay on bars
  @Input() valueType: '%' | 'number' | 'currency' | 'currencyShort' | '' = '';
  @Input() forceMaxVal?: number; // New: force the X-axis max value
  @Input() forceMinVal?: number; // New: force the X-axis min value
  @Input() xTickCount: number = 6; // New: number of ticks for the x-axis
  @Input() xTickValues?: number[]; // New: explicit tick values for the x-axis
  @Input() showYTicks: boolean = false; // New: show ticks on the y-axis
  @Input() showTooltipTotal: boolean = true; // New: show total in tooltip (stacked bars)
  @Input() colorYAxisLabels: boolean = true; // New: color y-axis labels based on bar color
  @Input() yAxisLabelColor?: string; // New: optional override for label color
  @Input() yAxisLabelSize: string = '14px'; // New: optional override for label font size
  @Input() maxLabelWidth: number = 80; // Width to wrap y-axis labels
  @Input() roundedCorners: boolean = true; // Optional rounding on corners
  @Input() cornerRadius?: number; // Custom corner radius for rounded corners
  @Input() yAxisTickPadding: number = 3; // Padding between y-axis tick and label
  @Input() legendOffset: number = 25; // Offset for legend when position is bottom

  // Grouped-bar mode: when groupSize > 1, every `groupSize` consecutive entries in `data`
  // are rendered as one group (e.g. 2 bars per category) with one shared y-axis label,
  // separated from the next group by `groupGapSlots` extra row-slots of blank space.
  // Default (groupSize = 1) reproduces the original single-bar-per-row layout exactly.
  @Input() groupSize: number = 1;
  @Input() groupGapSlots: number = 1;

  // Legend items for Stacked Charts (Optional, can be derived or passed explicitly)
  @Input() legendItems: { label: string, color: string }[] = [];
  @Input() disableNice: boolean = false; // New: disable d3 nice() for strict domain

  // Click-to-toggle legend, matching app-multi-stacked-bar-chart's interaction pattern:
  // clicking a legend entry hides/shows every bar (or stacked segment) sharing its color.
  hiddenLegendColors = new Set<string>();

  toggleLegendColor(color: string): void {
    if (this.hiddenLegendColors.has(color)) {
      this.hiddenLegendColors.delete(color);
    } else {
      this.hiddenLegendColors.add(color);
    }
    this.drawChart();
  }

  private isColorHidden(color: string | undefined): boolean {
    return !!color && this.hiddenLegendColors.has(color);
  }

  // A bar/segment's value, or 0 if its color has been toggled off via the legend.
  private effectiveValue = (d: BarData): number => {
    if (d.segments && d.segments.length > 0) {
      return d3.sum(d.segments.filter(s => !this.isColorHidden(s.color)), s => s.value);
    }
    return this.isColorHidden(d.color) ? 0 : d.value;
  };

  private chartId = Math.random().toString(36).substring(2, 9);
  private themeService = inject(ThemeService);
  private resizeObserver!: ResizeObserver;
  private themeSubscription?: Subscription;

  ngAfterViewInit() {
    this.resizeObserver = new ResizeObserver(() => this.drawChart());
    this.resizeObserver.observe(this.chartContainer.nativeElement);
    this.currentTheme = this.themeService.getTheme();
    this.themeSubscription = this.themeService.theme$.subscribe((theme) => {
      this.currentTheme = theme;
      this.drawChart(); // Redraw chart with new theme colors
    });
    this.drawChart();
  }

  ngOnDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data']) this.drawChart();
  }

  private drawChart() {
    if (!this.data?.length) return;
    const container: HTMLElement = this.chartContainer.nativeElement;
    const parent: HTMLElement | null = container.parentElement;
    if (!parent) return;

    // Clear old SVG + tooltip
    d3.select(container).selectAll('*').remove();
    d3.select(parent).selectAll('.chart-tooltip').remove();

    const colors = this.getColors();

    // Tooltip
    let tooltip: any;
    if (this.showTooltip) {
      tooltip = d3.select('body').select('.horizontal-tooltip');
      if (tooltip.empty()) {
        tooltip = d3
          .select('body')
          .append('div')
          .attr('class', 'horizontal-tooltip')
          .style('opacity', '0')
          .style('position', 'absolute')
          .style('pointer-events', 'none')
          .style('background', colors.background)
          .style('border', colors.border)
          .style('border-radius', '14px')
          .style('padding', '12px 16px')
          .style('box-shadow', colors.shadow)
          .style('color', colors.text)
          .style('font-family', 'Inter, sans-serif')
          .style('white-space', 'nowrap')
          .style('transition', 'opacity 0.15s ease') // removed transform
          .style('z-index', '9999') // high z-index
          .style('left', '-9999px')
          .style('top', '-9999px');
      }

      // Update tooltip styles dynamically (even if it already exists) to handle theme changes
      tooltip
        .style('background', colors.background)
        .style('border', colors.border)
        .style('box-shadow', colors.shadow)
        .style('color', colors.text);
    }

    let legHeight = 0;
    if (this.showLegend) {
      legHeight = 30;
    }

    const margin = {
      top: this.marginTop + (this.showLegend && this.legendPosition === 'top' ? legHeight + 10 : 0),
      right: this.showBarsValue ? 80 : 40,
      bottom: this.marginBottom + (this.showLegend && this.legendPosition === 'bottom' ? legHeight + 10 : 0),
      left: 120
    };
    const leftMargin = this.leftMargin || margin.left;
    const width = container.clientWidth - leftMargin - margin.right;

    // Safety check for width - if too small, don't draw (prevents inverted axis)
    if (width <= 0) return;

    const groupSize = Math.max(1, Math.floor(this.groupSize) || 1);
    const isGrouped = groupSize > 1;
    const groupGapSlots = Math.max(0, Math.round(this.groupGapSlots));
    const numGroups = Math.ceil(this.data.length / groupSize);
    const gapSlots = isGrouped ? Math.max(0, numGroups - 1) * groupGapSlots : 0;
    const totalSlots = this.data.length + gapSlots;
    // Maps a bar's index in `data` to its row-slot in the y-scale domain, leaving
    // `groupGapSlots` blank slots between consecutive groups.
    const slotOf = (i: number) => isGrouped ? i + Math.floor(i / groupSize) * groupGapSlots : i;

    const height = Math.max(totalSlots * this.barHeight, this.minHeight);

    const svg = d3
      .select(container)
      .append('svg')
      .attr('width', '100%')
      .attr('height', height + margin.top + margin.bottom);

    // DEFS for Gradients
    const defs = svg.append('defs');

    // Create gradients for each bar (only used for non-stacked)
    this.data.forEach((d, i) => {
      if (d.gradientStart && d.gradientEnd && !d.segments) {
        const gradientId = `bar-gradient-${this.chartId}-${i}`;
        const gradient = defs.append('linearGradient')
          .attr('id', gradientId)
          .attr('x1', '0%')
          .attr('y1', '0%')
          .attr('x2', '100%')
          .attr('y2', '100%'); // Diagonal for better visibility

        gradient.append('stop')
          .attr('offset', '0%')
          .attr('stop-color', d.gradientStart);

        gradient.append('stop')
          .attr('offset', '100%')
          .attr('stop-color', d.gradientEnd);
      }
    });

    // Create stripe patterns when showStripes is enabled
    if (this.showStripes) {
      const segmentColors = new Set<string>();
      this.data.forEach(d => {
        if (d.segments) {
          d.segments.forEach(s => segmentColors.add(s.color));
        } else if (d.color) {
          segmentColors.add(d.color);
        }
      });

      segmentColors.forEach(color => {
        const patternId = `stripe-${this.chartId}-${color.replace('#', '')}`;
        const pattern = defs.append('pattern')
          .attr('id', patternId)
          .attr('patternUnits', 'userSpaceOnUse')
          .attr('width', 8)
          .attr('height', 8)
          .attr('patternTransform', 'rotate(45)');

        pattern.append('rect')
          .attr('width', 8)
          .attr('height', 8)
          .attr('fill', color)
          .attr('fill-opacity', 1);

        pattern.append('line')
          .attr('x1', 0).attr('y1', 0)
          .attr('x2', 0).attr('y2', 8)
          .attr('stroke', 'rgba(255, 255, 255, 0.4)')
          .attr('stroke-width', 2);
      });
    }

    const mainGroup = svg.append('g')
      .attr('transform', `translate(${leftMargin}, ${margin.top})`);

    // Calculate padding if thickness is specified
    if (this.barThickness && this.barHeight) {
      const calculatedPadding = 1 - (this.barThickness / this.barHeight);
      this.barPadding = Math.max(0, calculatedPadding);
    }

    // Y scale — domain spans every row-slot (bars + inter-group gap slots when grouped)
    const y = d3
      .scaleBand()
      .domain(Array.from({ length: totalSlots }, (_, k) => k.toString()))
      .range([0, height])
      .padding(this.barPadding);

    // X scale
    // If stacked, maxVal is the sum of segments, which ideally should be d.value
    // We trust d.value is correct total, or we re-calculate it.
    // For safety, let's max of (d.value OR sum of segments)
    const maxVal = this.forceMaxVal || d3.max(this.data, (d) => this.effectiveValue(d)) || 1;

    // Find the minimum value (it can be less than 0)
    const minValRaw = this.forceMinVal !== undefined ? this.forceMinVal : (d3.min(this.data, (d) => {
      if (d.segments && d.segments.length > 0) {
        const visibleSegments = d.segments.filter(s => !this.isColorHidden(s.color));
        return visibleSegments.length ? d3.min(visibleSegments, s => s.value) : 0;
      }
      return this.isColorHidden(d.color) ? 0 : d.value;
    }) || 0);
    const minVal = Math.min(0, minValRaw);
    const hasNegative = minVal < 0;

    // Use sqrt scale for better visualization of wide-ranging data, or linear scale
    const xBase = (this.scaleType === 'sqrt' && !hasNegative)
      ? d3.scaleSqrt().domain([minVal, maxVal]).range([0, width])
      : d3.scaleLinear().domain([minVal, maxVal]).range([0, width]);

    const x = this.disableNice ? xBase : xBase.nice();

    // DASHED X GRID
    let gridTickValues: number[] | undefined;

    if (this.scaleType === 'sqrt') {
      const sqrtMax = Math.sqrt(maxVal);
      const numTicks = 6;
      gridTickValues = [];
      for (let i = 0; i <= numTicks; i++) {
        const sqrtValue = (sqrtMax / numTicks) * i;
        const value = Math.round(sqrtValue * sqrtValue);
        gridTickValues.push(value);
      }
      gridTickValues = [...new Set(gridTickValues)].sort((a, b) => a - b);
    }

    const xGrid = d3
      .axisBottom(x)
      .tickSize(-height)
      .tickFormat(() => '');

    let finalGridTickValues = gridTickValues;
    if (this.xTickValues && this.xTickValues.length) {
      finalGridTickValues = this.xTickValues;
    }

    if (finalGridTickValues) {
      xGrid.tickValues(finalGridTickValues);
    } else {
      xGrid.ticks(this.xTickCount);
    }

    if (this.showXAxisGrid) {
      mainGroup
        .append('g')
        .attr('class', 'x-grid')
        .attr('transform', `translate(0, ${height})`)
        .call(xGrid)
        .selectAll('line')
        .attr('stroke', colors.axisLine)
        .attr('stroke-dasharray', '4 4')
        .attr('stroke-width', 1);

      mainGroup.select('.x-grid .domain').remove();
    }

    // ZERO LINE
    if (hasNegative) {
      mainGroup
        .append('line')
        .attr('class', 'zero-line')
        .attr('x1', x(0))
        .attr('x2', x(0))
        .attr('y1', 0)
        .attr('y2', height)
        .attr('stroke', colors.axisLine)
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', 'none')
        .attr('opacity', 0.8);
    }

    // ZERO LINE
    if (hasNegative) {
      mainGroup
        .append('line')
        .attr('class', 'zero-line')
        .attr('x1', x(0))
        .attr('x2', x(0))
        .attr('y1', 0)
        .attr('y2', height)
        .attr('stroke', colors.axisLine)
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', 'none')
        .attr('opacity', 0.8);
    }

    // DASHED Y GRID
    if (this.showYAxisGrid) {
      const yGridAxis = d3.axisLeft(y).tickSize(-width).tickFormat(() => '');
      if (isGrouped) {
        yGridAxis.tickValues(this.data.map((d, i) => slotOf(i).toString()));
      }
      mainGroup
        .append('g')
        .attr('class', 'y-grid')
        .call(yGridAxis)
        .selectAll('line')
        .attr('stroke', colors.axisLine)
        .attr('stroke-dasharray', '4 4')
        .attr('stroke-opacity', 0.6);

      mainGroup.select('.y-grid .domain').remove();
    }

    // LEFT LABELS
    if (this.showYAxisLabel && isGrouped) {
      // Grouped mode: one shared, vertically-centered label per group instead of per-row —
      // d3.axisLeft can't do this natively, so ticks are hand-drawn against the same y-scale.
      const dataList = this.data;
      const labelColorMap = new Map(this.data.map((d, idx) => [idx.toString(), d.color]));
      const maxLabelWidth = this.maxLabelWidth;
      const bandwidth = y.bandwidth();

      for (let g = 0; g < numGroups; g++) {
        const startIdx = g * groupSize;
        const endIdx = Math.min(startIdx + groupSize, this.data.length) - 1;
        const yStart = y(slotOf(startIdx).toString())!;
        const yEnd = y(slotOf(endIdx).toString())! + bandwidth;
        const centerY = (yStart + yEnd) / 2;
        const labelText = String(dataList[startIdx]?.label || '');

        const textEl = mainGroup.append('text')
          .attr('text-anchor', 'end')
          .attr('dominant-baseline', 'middle')
          .style('font-size', this.yAxisLabelSize)
          .style('font-weight', '500')
          .style('pointer-events', 'none')
          .attr('fill', this.yAxisLabelColor ? this.yAxisLabelColor : (this.colorYAxisLabels ? (labelColorMap.get(String(startIdx)) || colors.labelColor) : colors.labelColor));

        const words = labelText.split(/\s+/);
        const lines: string[] = [];
        let currentLine: string[] = [];
        const measureTspan = textEl.append('tspan').attr('x', -this.yAxisTickPadding).attr('y', centerY);
        words.forEach((word) => {
          currentLine.push(word);
          measureTspan.text(currentLine.join(' '));
          if (maxLabelWidth > 0 && measureTspan.node()!.getComputedTextLength() > maxLabelWidth) {
            if (currentLine.length > 1) {
              currentLine.pop();
              lines.push(currentLine.join(' '));
              currentLine = [word];
            } else {
              lines.push(currentLine.join(' '));
              currentLine = [];
            }
          }
        });
        if (currentLine.length > 0) lines.push(currentLine.join(' '));
        measureTspan.remove();

        const lineSpacing = 1.1;
        lines.forEach((lineText, li) => {
          textEl.append('tspan')
            .attr('x', -this.yAxisTickPadding)
            .attr('y', centerY)
            .attr('dy', `${(li - (lines.length - 1) / 2) * lineSpacing}em`)
            .text(lineText);
        });
      }

      if (this.showYAxisLine) {
        mainGroup.append('line')
          .attr('x1', 0).attr('x2', 0).attr('y1', 0).attr('y2', height)
          .attr('stroke', '#94a3b8')
          .attr('stroke-width', 1)
          .attr('stroke-opacity', 1);
      }
    } else if (this.showYAxisLabel) {
      const dataList = this.data;
      const yAxis = mainGroup.append('g').call(
        d3.axisLeft(y)
          .tickSize(this.showYTicks ? 5 : 0)
          .tickPadding(this.yAxisTickPadding)
          .tickFormat((indexStr: any) => {
            const idx = parseInt(String(indexStr), 10);
            return dataList[idx]?.label || '';
          })
      );

      if (this.showYTicks) {
        yAxis.selectAll('.tick line')
          .attr('stroke', '#94a3b8') // Darker slate for ticks
          .attr('stroke-width', 1);
      }
      const labelColorMap = new Map(this.data.map((d, idx) => [idx.toString(), d.color]));
      const maxLabelWidth = this.maxLabelWidth;
      yAxis
        .selectAll('text')
        .each(function (d) {
          const textEl = this as SVGTextElement;
          const idx = parseInt(String(d), 10);
          const labelText = String(dataList[idx]?.label || d);
          const words = labelText.split(/\s+/);
          const lineSpacing = 1.1;
          const x = textEl.getAttribute('x') || '0';
          const yPos = textEl.getAttribute('y') || '0';
          const dy = parseFloat(textEl.getAttribute('dy') || '0.32');

          // Wrap text logic...
          let lines: string[] = [];
          d3.select(textEl).text(null);
          let tspan = d3.select(textEl).append('tspan').attr('x', x).attr('y', yPos);
          let currentLine: string[] = [];

          words.forEach((word) => {
            currentLine.push(word);
            tspan.text(currentLine.join(' '));
            if (maxLabelWidth > 0 && tspan.node()!.getComputedTextLength() > maxLabelWidth) {
              if (currentLine.length > 1) {
                currentLine.pop();
                lines.push(currentLine.join(' '));
                currentLine = [word];
                tspan.text(word);
              } else {
                lines.push(currentLine.join(' '));
                currentLine = [];
                tspan.text('');
              }
            }
          });
          if (currentLine.length > 0) lines.push(currentLine.join(' '));
          tspan.remove();

          lines.forEach((lineText, i) => {
            d3.select(textEl)
              .append('tspan')
              .attr('x', x)
              .attr('y', yPos)
              .attr('dy', `${dy + (i - (lines.length - 1) / 2) * lineSpacing}em`)
              .text(lineText);
          });
        })
        .attr('fill', (d: any) => this.yAxisLabelColor ? this.yAxisLabelColor : (this.colorYAxisLabels ? (labelColorMap.get(String(d)) || colors.labelColor) : colors.labelColor))
        .style('font-size', this.yAxisLabelSize)
        .style('font-weight', '500')
        .style('pointer-events', 'none');

      if (this.showYAxisLine) {
        yAxis
          .select('.domain')
          .attr('stroke', '#94a3b8') // Darker slate for axis line
          .attr('stroke-width', 1)
          .attr('stroke-opacity', 1);
      } else {
        yAxis.select('.domain').remove();
      }
    }

    // --- DRAW BARS ---
    // We now handle both Single and Stacked bars.
    // For stacking, we manually draw segments.

    const barGroups = mainGroup.selectAll('.bar-group')
      .data(this.data)
      .enter()
      .append('g')
      .attr('class', 'bar-group')
      .attr('transform', (d, i) => {
        const bandwidth = y.bandwidth();
        const barThickness = this.maxBarWidth > 0 ? Math.min(bandwidth, this.maxBarWidth) : bandwidth;
        const yOffset = (bandwidth - barThickness) / 2;
        return `translate(0, ${y(slotOf(i).toString())! + yOffset})`;
      });

    barGroups.each((d, i, nodes) => {
      const group = d3.select(nodes[i]);
      const bandwidth = y.bandwidth();
      const barThickness = this.maxBarWidth > 0 ? Math.min(bandwidth, this.maxBarWidth) : bandwidth;
      const barHeight = barThickness;

      // Add a clip path to act as "overflow: hidden" with rounded corners for the full width
      const clipId = `bar-clip-${this.chartId}-${i}`;
      const clipR = this.roundedCorners ? (this.cornerRadius ?? 10) : 0;
      const clipPathD = `
        M 0 0
        H ${width - clipR}
        Q ${width} 0 ${width} ${clipR}
        V ${barHeight - clipR}
        Q ${width} ${barHeight} ${width - clipR} ${barHeight}
        H 0
        Z
      `;

      defs.append('clipPath')
        .attr('id', clipId)
        .append('path')
        .attr('d', clipPathD);

      const innerGroup = group.append('g').attr('clip-path', `url(#${clipId})`);

      if (d.segments && d.segments.length > 0) {
        // --- STACKED LOGIC ---
        let currentX = x(0);

        d.segments.forEach((seg, segIndex) => {
          const segWidth = this.isColorHidden(seg.color) ? 0 : Math.abs(x(seg.value) - x(0));
          const isFirst = segIndex === 0;
          const isLast = segIndex === d.segments!.length - 1;

          let path = '';
          let startPath = '';

          // We are drawing relative to the group translation (0, y).
          // So y is 0 here.

          // If width is tiny, just draw a rect
          if (segWidth < 2) {
            innerGroup.append('rect')
              .attr('x', currentX)
              .attr('y', 0)
              .attr('width', 0)
              .attr('height', barHeight)
              .attr('fill', seg.color)
              .transition()
              .duration(1000)
              .ease(d3.easeCubicOut)
              .attr('width', segWidth);
            currentX += segWidth;
            return;
          }

          if (isLast) {
            // Rounded right
            const r = this.roundedCorners ? Math.min(this.cornerRadius ?? 8, segWidth / 2, barHeight / 2) : 0;
            path = `
                      M ${currentX} 0
                      H ${currentX + segWidth - r}
                      Q ${currentX + segWidth} 0 ${currentX + segWidth} ${r}
                      V ${barHeight - r}
                      Q ${currentX + segWidth} ${barHeight} ${currentX + segWidth - r} ${barHeight}
                      H ${currentX}
                      Z
                    `;
            startPath = `
                      M ${currentX} 0
                      H ${currentX}
                      Q ${currentX} 0 ${currentX} 0
                      V ${barHeight}
                      Q ${currentX} ${barHeight} ${currentX} ${barHeight}
                      H ${currentX}
                      Z
                    `;
          } else if (isFirst) {
            path = `
                      M ${currentX} 0
                      H ${currentX + segWidth}
                      V ${barHeight}
                      H ${currentX}
                      Z
                   `;
            startPath = `
                      M ${currentX} 0
                      H ${currentX}
                      V ${barHeight}
                      H ${currentX}
                      Z
                   `;
          } else {
            // Middle - flat both sides
            path = `
                      M ${currentX} 0
                      H ${currentX + segWidth}
                      V ${barHeight}
                      H ${currentX}
                      Z
                   `;
            startPath = `
                      M ${currentX} 0
                      H ${currentX}
                      V ${barHeight}
                      H ${currentX}
                      Z
                   `;
          }

          innerGroup.append('path')
            .attr('d', startPath)
            .attr('fill', this.showStripes
              ? `url(#stripe-${this.chartId}-${seg.color.replace('#', '')})`
              : seg.color
            )
            // Tooltip listener per segment
            .on('mousemove', (event: MouseEvent) => {
              if (!this.showTooltip || !tooltip) return;
              const allSegmentsHtml = d.segments!.map(s => `
                            <div style="display:flex;justify-content:space-between;align-items:center;gap:12px;">
                                <div style="display:flex;align-items:center;gap:6px;">
                                    <span style="width:8px;height:8px;border-radius:2px;background-color:${s.color}"></span>
                                    <span style="font-size:13px;color:${colors.text};">${s.label}</span>
                                </div>
                                <span style="font-size:13px;font-weight:600;text-align:right;color:${colors.text};">${this.valueType === 'currencyShort' ? '$' + Number(s.value).toLocaleString('en-US') : this.formatAxisValue(s.value)}</span>
                            </div>
                        `).join('');

              tooltip
                .style('opacity', '1')
                .html(
                  `
                            <div style="display:flex;flex-direction:column;gap:4px;">
                              <div style="font-size:13px;font-weight:600;color:${colors.text};border-bottom:1px solid ${colors.border};">
                                ${d.tooltipLabel || d.label}
                              </div>
                              ${allSegmentsHtml}
                              ${this.showTooltipTotal ? `
                              <div style="display:flex;justify-content:space-between;align-items:center;">
                                <div style="display:flex;align-items:center;gap:6px;">
                                    <span style="width:8px;height:8px;border-radius:2px;background-color:${d.segments && d.segments.length > 0 ? d.segments[0].color : (d.color || '#8b5cf6')}"></span>
                                    <span style="font-size:13px;font-weight:500;color:${colors.subtitleColor}">Total</span>
                                </div>
                                <span style="font-size:13px;font-weight:700;color:${colors.text}">
                                   ${d.tooltipTotalValue !== undefined ? d.tooltipTotalValue : (this.valueType === 'currencyShort' ? '$' + Number(d3.sum(d.segments!, s => s.value)).toLocaleString('en-US') : this.formatAxisValue(d3.sum(d.segments!, s => s.value)))}
                                </span>
                              </div>
                              ` : ''}
                            </div>
                            `
                );

              // Clamp to viewport to prevent overflow scrollbars
              const node = tooltip.node() as HTMLElement;
              const tw = node.offsetWidth;
              const th = node.offsetHeight;
              const vw = window.innerWidth;
              const vh = window.innerHeight;
              const containerRect = container.getBoundingClientRect();
              let rightBoundary = vw;
              const pathRect = (event.target as Element).getBoundingClientRect();
              const hoverPct = (event.clientX - pathRect.left) / (pathRect.width || 1);
              let left = event.pageX + 16;
              if (hoverPct > 0.8 || event.pageX + 16 + tw > rightBoundary + window.scrollX) {
                left = event.pageX - tw - 16;
              }
              const isBottomBar = i === this.data.length - 1;
              const overflowBottom = event.pageY - 10 + th > vh + window.scrollY;
              let top = (isBottomBar || overflowBottom) ? event.pageY - th - 10 : event.pageY - 10;
              if (top < window.scrollY + 10) {
                top = window.scrollY + 10;
              }
              tooltip.style('left', `${left}px`).style('top', `${top}px`);
            })
            .on('mouseleave', () => {
              if (!this.showTooltip || !tooltip) return;
              tooltip.style('opacity', '0').style('transform', 'translateY(-4px)');
            })
            .transition()
            .duration(1000)
            .ease(d3.easeCubicOut)
            .attr('d', path);

          currentX += segWidth;
        });

      } else {
        // --- SINGLE BAR LOGIC ---
        const isHidden = this.isColorHidden(d.color);
        const effVal = isHidden ? 0 : d.value;
        const xOrigin = x(0);
        const xVal = x(effVal);
        const isNegative = effVal < 0;

        const rawWidth = Math.abs(xVal - xOrigin);
        const barWidth = isHidden ? 0 : Math.max(rawWidth, Math.abs(effVal) > 0 ? 5 : 2);
        const startX = isNegative ? xOrigin - barWidth : xOrigin;

        const r = this.roundedCorners ? Math.min(this.cornerRadius ?? 10, barWidth / 2) : 0;

        let path = '';
        let startPath = '';
        if (isNegative) {
          path = `
            M ${startX + barWidth} 0
            H ${startX + r}
            Q ${startX} 0 ${startX} ${r}
            V ${barHeight - r}
            Q ${startX} ${barHeight} ${startX + r} ${barHeight}
            H ${startX + barWidth}
            Z
          `;
          startPath = `
            M ${startX + barWidth} 0
            H ${startX + barWidth}
            Q ${startX + barWidth} 0 ${startX + barWidth} 0
            V ${barHeight}
            Q ${startX + barWidth} ${barHeight} ${startX + barWidth} ${barHeight}
            H ${startX + barWidth}
            Z
          `;
        } else {
          path = `
            M ${startX} 0
            H ${startX + barWidth - r}
            Q ${startX + barWidth} 0 ${startX + barWidth} ${r}
            V ${barHeight - r}
            Q ${startX + barWidth} ${barHeight} ${startX + barWidth - r} ${barHeight}
            H ${startX}
            Z
          `;
          startPath = `
            M ${startX} 0
            H ${startX}
            Q ${startX} 0 ${startX} 0
            V ${barHeight}
            Q ${startX} ${barHeight} ${startX} ${barHeight}
            H ${startX}
            Z
          `;
        }

        innerGroup.append('path')
          .attr('d', startPath)
          .attr('fill', d.gradientStart && d.gradientEnd
            ? `url(#bar-gradient-${this.chartId}-${i})`
            : (d.color || colors.sliceColors[i % colors.sliceColors.length])
          )
          // Tooltip listener
          .on('mousemove', (event: MouseEvent) => {
            if (!this.showTooltip || !tooltip) return;
            tooltip
              .style('opacity', '1')
              .html(
                `
                        <div style="display:flex;flex-direction:column;gap:4px;">
                          <div style="font-size:14px;font-weight:600;color:${colors.text};margin-bottom:4px;border-bottom:1px solid ${colors.border};padding-bottom:4px;">
                            ${d.tooltipLabel || d.label}
                          </div>
                          <div style="display:flex;align-items:center;gap:6px;">
                            <span style="width:8px;height:8px;border-radius:2px;background-color:${d.color || colors.sliceColors[i % colors.sliceColors.length]}"></span>
                            <span style="font-size:13px;font-weight:600;color:${colors.text};">${d.tooltipValueLabel !== undefined ? d.tooltipValueLabel : `${this.valueType === 'currencyShort' ? '$' + Number(d.value).toLocaleString('en-US') : this.formatAxisValue(d.value)} ${this.xAxisTitle || ''}`}</span>
                          </div>
                        </div>
                        `
              );

            // Clamp to viewport to prevent overflow scrollbars
            const node = tooltip.node() as HTMLElement;
            const tw = node.offsetWidth;
            const th = node.offsetHeight;
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const containerRect = container.getBoundingClientRect();
            let rightBoundary = vw;
            const pathRect = (event.target as Element).getBoundingClientRect();
            const hoverPct = (event.clientX - pathRect.left) / (pathRect.width || 1);
            let left = event.pageX + 16;
            if (hoverPct > 0.8 || event.pageX + 16 + tw > rightBoundary + window.scrollX) {
              left = event.pageX - tw - 16;
            }
            const isBottomBar = i === this.data.length - 1;
            const overflowBottom = event.pageY - 10 + th > vh + window.scrollY;
            let top = (isBottomBar || overflowBottom) ? event.pageY - th - 10 : event.pageY - 10;
            if (top < window.scrollY + 10) {
              top = window.scrollY + 10;
            }
            tooltip.style('left', `${left}px`).style('top', `${top}px`);
          })
          .on('mouseleave', () => {
            if (!this.showTooltip || !tooltip) return;
            tooltip.style('opacity', '0');
          })
          .transition()
          .duration(1000)
          .ease(d3.easeCubicOut)
          .attr('d', path);
      }
    });

    // VALUES AT END OF BARS (Total Value)
    if (this.showBarsValue) {
      const textEls = barGroups
        .append('text')
        .attr('x', (d) => {
          return x(0); // Start position at x=0
        })
        .attr('y', (d) => {
          const bandwidth = y.bandwidth();
          const barThickness = this.maxBarWidth > 0 ? Math.min(bandwidth, this.maxBarWidth) : bandwidth;
          return barThickness / 2;
        })
        .attr('dominant-baseline', 'middle')
        .style('font-size', '12px')
        .style('fill', colors.labelColor)
        .style('font-weight', '500')
        .style('opacity', 0) // Start with opacity 0
        .text((d) => d.displayValue !== undefined ? d.displayValue : this.formatAxisValue(d.value));

      textEls.append('title')
        .text((d) => d.displayValue !== undefined ? d.displayValue : this.formatAxisValue(d.value));

      textEls.transition()
        .duration(1000)
        .ease(d3.easeCubicOut)
        .attr('x', (d) => {
          const val = d.segments ? d3.sum(d.segments, s => s.value) : d.value;
          return x(val) + 16;
        })
        .style('opacity', 1);
    }

    // X AXIS
    if (this.showXAxisLabel) {
      let tickValues: number[] | undefined;
      if (this.scaleType === 'sqrt') {
        const sqrtMax = Math.sqrt(maxVal);
        const numTicks = 6;
        tickValues = [];
        for (let i = 0; i <= numTicks; i++) {
          tickValues.push(Math.round(Math.pow((sqrtMax / numTicks) * i, 2)));
        }
        tickValues = [...new Set(tickValues)].sort((a, b) => a - b);
      }

      let finalTickValues = tickValues;
      if (this.xTickValues && this.xTickValues.length) {
        finalTickValues = this.xTickValues;
      }

      const xAxisScale = finalTickValues
        ? d3.axisBottom(x).tickValues(finalTickValues)
        : d3.axisBottom(x).ticks(this.xTickCount);

      xAxisScale.tickFormat((d: any) => this.formatAxisValue(Number(d)));

      const xAxis = mainGroup
        .append('g')
        .attr('class', 'x-axis')
        .attr('transform', `translate(0, ${height})`)
        .call(xAxisScale);

      xAxis.selectAll('text')
        .style('fill', colors.labelColor)
        .style('font-size', '12px')
        .style('font-family', 'Lato, sans-serif');

      xAxis.select('path.domain')
        .attr('stroke', colors.axisLine)
        .attr('stroke-width', 1)
        .attr('stroke-opacity', 0.6);

      if (this.xAxisTitle) {
        mainGroup.append('text')
          .attr('class', 'x-axis-title')
          .attr('x', width / 2)
          .attr('y', height + 45)
          .attr('text-anchor', 'middle')
          .style('fill', colors.text)
          .style('font-size', '14px')
          .style('font-weight', '600')
          .text(this.xAxisTitle);
      }
    }

    // LEGEND
    if (this.showLegend) {
      const legendGroup = svg.append('g').attr('class', 'chart-legend');
      const itemGap = 20;
      const circleRadius = 5;
      const fontSize = 12;

      // Determine items to show: if passed explicitly, use those. 
      // Else if stacked, we probably need manual legend data passed in.
      // Else use data labels (default behavior for single bars)

      let legendData: { label: string, color: string }[] = [];

      if (this.legendItems && this.legendItems.length > 0) {
        legendData = this.legendItems;
      } else {
        // Default to labeled bars
        legendData = this.data.map((d, i) => ({
          label: d.label,
          color: d.color || d.gradientStart || colors.sliceColors[i % colors.sliceColors.length]
        }));
      }

      const tempGroup = svg.append('g').style('visibility', 'hidden');
      const measuredItems = legendData.map((d) => {
        const textToMeasure = tempGroup.append('text').attr('font-size', fontSize).text(d.label);
        const textWidth = textToMeasure.node()?.getComputedTextLength() || 0;
        return { ...d, width: circleRadius * 2 + 8 + textWidth };
      });
      tempGroup.remove();

      const totalLegendWidth = measuredItems.reduce((acc, item, i) =>
        acc + item.width + (i < measuredItems.length - 1 ? itemGap : 0), 0);

      let startX = Math.max(10, (container.clientWidth - totalLegendWidth) / 2);
      const legendY = this.legendPosition === 'bottom' ? height + margin.top + this.legendOffset : 20;

      measuredItems.forEach((d) => {
        const g = legendGroup.append('g')
          .attr('transform', `translate(${startX}, ${legendY})`)
          .attr('opacity', this.isColorHidden(d.color) ? 0.4 : 1)
          .style('cursor', 'pointer')
          .on('click', () => this.toggleLegendColor(d.color));
        g.append('circle').attr('r', circleRadius).attr('fill', d.color);
        g.append('text').attr('x', circleRadius + 8).attr('y', 4).style('font-size', `${fontSize}px`)
          .style('font-weight', '500')
          .style('fill', colors.labelColor)
          .style('user-select', 'none')
          .text(d.label);
        startX += d.width + itemGap;
      });
    }
  }

  private formatAxisValue(value: number): string {
    // Format to max 2 decimal places, stripping trailing zeros
    const formattedVal = Number.isInteger(value) ? value : Number(value.toFixed(2));
    const formattedStr = formattedVal.toLocaleString('en-US');

    if (this.valueType === '%') {
      return `${formattedStr}%`;
    }
    if (this.valueType === 'number') {
      return formattedStr;
    }
    if (this.valueType === 'currency') {
      return `$${formattedStr}`;
    }
    if (this.valueType === 'currencyShort') {
      const absNum = Math.abs(value);
      if (absNum >= 1000000) {
        return `$${(value / 1000000).toFixed(2).replace(/\.00$/, '')}M`;
      } else if (absNum >= 1000) {
        return `$${(value / 1000).toFixed(2).replace(/\.00$/, '')}K`;
      }
      return `$${formattedStr}`;
    }
    return formattedStr;
  }

  private getColors() {
    const isDark = this.currentTheme === 'dark';

    if (isDark) {
      return {
        background: '#1e293b', // Slate 800
        text: '#f8fafc',
        shadow: '0 10px 15px -3px rgba(0,0,0,0.5)',
        border: '1px solid #334155',
        subtitleColor: '#94a3b8',
        sliceColors: ['#7C3AED', '#3b82f6', '#10B981', '#F59E0B', '#fb923c', '#EC4899', '#ef4444', '#0ea5e9'],
        axisLine: '#475569',
        labelColor: '#A1A1AA',
      };
    } else {
      // Light Theme - Standard Light Tooltip
      return {
        background: '#FFFFFF', // White
        text: '#1e293b', // Slate 800
        shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        border: '1px solid #e2e8f0', // Slate 200
        subtitleColor: '#64748b', // Slate 500
        sliceColors: ['#7C3AED', '#3b82f6', '#10B981', '#F59E0B', '#fb923c', '#EC4899', '#ef4444', '#0ea5e9'],
        axisLine: '#d1d5db',
        labelColor: '#64748b',
      };
    }
  }

}
